import { useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { getOpenAIKey } from "@/lib/geniusLabsStore";
import { type UserStory, type Stakeholder } from "@/lib/projectStore";
import {
  Upload, FileText, Loader2, CheckCircle2, Circle, AlertTriangle,
  Users, Zap, ListTodo, CheckSquare, Clock, XCircle, ChevronDown,
  ChevronRight, Sparkles, ClipboardList, ArrowRight, Info,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

// ── Types ─────────────────────────────────────────────────────────────────────
interface HandoverItem {
  id: string;
  title: string;
  detail: string;
  confidence?: "high" | "medium" | "low";
  priority?: "high" | "medium" | "low";
  owner?: string;
  blocker?: string;
}

interface HandoverRisk {
  id: string;
  description: string;
  severity: "high" | "medium" | "low";
  mitigation: string;
}

interface HandoverStakeholder {
  id: string;
  name: string;
  role: string;
  concern: string;
  action: "reach out" | "inform" | "manage";
}

interface FocusAction {
  action: string;
  reason: string;
  day: 1 | 2 | 3;
}

interface HandoverReport {
  project_title: string;
  summary: string;
  role_context: string;
  completed: HandoverItem[];
  in_progress: HandoverItem[];
  not_started: HandoverItem[];
  risks: HandoverRisk[];
  stakeholders: HandoverStakeholder[];
  focus_this_week: FocusAction[];
  open_questions: string[];
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface Props {
  projectId: string;
  baProjectId: string | null;
  onStoriesAdded: (stories: UserStory[]) => void;
  onStakeholdersAdded: (stakeholders: Stakeholder[]) => void;
}

// ── AI prompt ─────────────────────────────────────────────────────────────────
const SYSTEM_PROMPT = `You are an expert project analyst helping someone who has just inherited an unfinished project.
Analyse all provided documents, notes, emails, exports, and artefacts to build a complete picture of the project state.

Return ONLY valid JSON with this exact structure:
{
  "project_title": "inferred project name",
  "summary": "2-3 sentence plain-English overview of where the project stands",
  "role_context": "what role the previous person had and what they were working on",
  "completed": [
    { "id": "c1", "title": "short title", "detail": "what was done and how it was verified", "confidence": "high|medium|low" }
  ],
  "in_progress": [
    { "id": "p1", "title": "short title", "detail": "what stage it is at", "owner": "person if mentioned", "blocker": "what is blocking it or null" }
  ],
  "not_started": [
    { "id": "n1", "title": "short title", "detail": "what needs to be done", "priority": "high|medium|low" }
  ],
  "risks": [
    { "id": "r1", "description": "risk description", "severity": "high|medium|low", "mitigation": "suggested mitigation" }
  ],
  "stakeholders": [
    { "id": "s1", "name": "name", "role": "their role", "concern": "what they care about", "action": "reach out|inform|manage" }
  ],
  "focus_this_week": [
    { "action": "specific action to take", "reason": "why this is urgent", "day": 1 }
  ],
  "open_questions": ["unanswered question 1", "unanswered question 2"]
}

Rules:
- Be specific — use names, dates, and details from the documents
- completed = evidence it is actually done (tested, merged, signed off, deployed)
- in_progress = started but not finished (branch exists, draft written, in review)
- not_started = mentioned or planned but no evidence of work begun
- focus_this_week: exactly 3-5 actions, assigned to day 1, 2, or 3
- open_questions: things that were never documented or resolved`;

const CONFIDENCE_COLOR: Record<string, string> = {
  high: "text-emerald-600 bg-emerald-500/10 border-emerald-500/20",
  medium: "text-amber-600 bg-amber-500/10 border-amber-500/20",
  low: "text-rose-600 bg-rose-500/10 border-rose-500/20",
};

const SEVERITY_DOT: Record<string, string> = {
  high: "bg-rose-500", medium: "bg-amber-500", low: "bg-emerald-500",
};

const ACTION_STYLE: Record<string, string> = {
  "reach out": "text-indigo-600 bg-indigo-500/10 border-indigo-500/20",
  "inform": "text-sky-600 bg-sky-500/10 border-sky-500/20",
  "manage": "text-amber-600 bg-amber-500/10 border-amber-500/20",
};

const DAY_COLOR: Record<number, string> = {
  1: "bg-rose-500/15 text-rose-700 border-rose-500/30",
  2: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  3: "bg-sky-500/15 text-sky-700 border-sky-500/30",
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function HandoverPanel({ projectId, baProjectId, onStoriesAdded, onStakeholdersAdded }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [files, setFiles] = useState<{ name: string; text: string }[]>([]);
  const [pastedText, setPastedText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<HandoverReport | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(["focus", "in_progress", "not_started"]));

  // Selection state for push
  const [selInProgress, setSelInProgress] = useState<Set<string>>(new Set());
  const [selNotStarted, setSelNotStarted] = useState<Set<string>>(new Set());
  const [selStakeholders, setSelStakeholders] = useState<Set<string>>(new Set());
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState<{ kanban: number; requirements: number; stakeholders: number } | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  // ── File reading ────────────────────────────────────────────────────────────
  const readFile = (file: File): Promise<string> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string || "");
      reader.onerror = () => resolve("");
      reader.readAsText(file);
    });

  const handleFiles = async (incoming: File[]) => {
    const readable = incoming.filter((f) =>
      /\.(txt|md|csv|json|log|xml|html|eml|msg|rtf)$/i.test(f.name) ||
      f.type.startsWith("text/")
    );
    const unreadable = incoming.filter((f) => !readable.includes(f));

    const results = await Promise.all(
      readable.map(async (f) => ({ name: f.name, text: await readFile(f) }))
    );
    setFiles((prev) => [...prev, ...results.filter((r) => r.text.trim())]);

    if (unreadable.length > 0) {
      setError(
        `${unreadable.map((f) => f.name).join(", ")} — PDF/Word files can't be read directly. Copy-paste their content into the text area below.`
      );
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (name: string) => setFiles((prev) => prev.filter((f) => f.name !== name));

  // ── Analyse ─────────────────────────────────────────────────────────────────
  const handleAnalyse = async () => {
    const key = getOpenAIKey();
    if (!key) {
      setError("OpenAI API key not set — add it in Genius Labs → Settings.");
      return;
    }
    const allText = [
      ...files.map((f) => `=== FILE: ${f.name} ===\n${f.text}`),
      pastedText.trim() ? `=== PASTED CONTENT ===\n${pastedText}` : "",
    ].filter(Boolean).join("\n\n");

    if (!allText.trim()) { setError("Add at least one file or paste some content first."); return; }

    setAnalysing(true); setError(null);
    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${key}` },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          response_format: { type: "json_object" },
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            { role: "user", content: allText.substring(0, 120000) },
          ],
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error?.message || `OpenAI error ${res.status}`); }
      const data = await res.json();
      const raw: HandoverReport = JSON.parse(data.choices[0].message.content);
      // Ensure IDs exist
      ["completed","in_progress","not_started","risks","stakeholders"].forEach((key) => {
        ((raw as any)[key] || []).forEach((item: any, i: number) => { if (!item.id) item.id = `${key[0]}${i}`; });
      });
      setReport(raw);
      // Pre-select all in-progress and not-started
      setSelInProgress(new Set((raw.in_progress || []).map((i) => i.id)));
      setSelNotStarted(new Set((raw.not_started || []).map((i) => i.id)));
      setSelStakeholders(new Set((raw.stakeholders || []).map((i) => i.id)));
    } catch (err: any) {
      setError(err.message || "Analysis failed.");
    } finally { setAnalysing(false); }
  };

  // ── Push ────────────────────────────────────────────────────────────────────
  const handlePush = async () => {
    if (!report) return;
    setPushing(true);
    let kanbanCount = 0, reqCount = 0, stakeholderCount = 0;

    // Push to Kanban (in-progress + not-started → user stories)
    const selectedIP = (report.in_progress || []).filter((i) => selInProgress.has(i.id));
    const selectedNS = (report.not_started || []).filter((i) => selNotStarted.has(i.id));

    const newStories: UserStory[] = [
      ...selectedIP.map((item) => ({
        id: crypto.randomUUID(),
        title: item.title,
        asA: "team member",
        iWant: item.detail,
        soThat: "the project is completed",
        acceptanceCriteria: item.blocker ? [`Resolve blocker: ${item.blocker}`] : [],
        priority: "must" as const,
        status: "in-progress" as const,
        createdAt: new Date().toISOString(),
      })),
      ...selectedNS.map((item) => ({
        id: crypto.randomUUID(),
        title: item.title,
        asA: "team member",
        iWant: item.detail,
        soThat: "the project is completed",
        acceptanceCriteria: [],
        priority: (item.priority === "high" ? "must" : item.priority === "medium" ? "should" : "could") as any,
        status: "backlog" as const,
        createdAt: new Date().toISOString(),
      })),
    ];
    if (newStories.length > 0) { onStoriesAdded(newStories); kanbanCount = newStories.length; }

    // Push requirements (not-started to backend)
    if (baProjectId && selectedNS.length > 0) {
      for (const item of selectedNS) {
        try {
          await axios.post(`${BASE}/api/tools/requirements`, {
            project_id: baProjectId,
            title: item.title,
            raw_text: item.detail,
            req_type: "functional",
            priority: item.priority === "high" ? "must" : item.priority === "medium" ? "should" : "could",
          }, { headers: authHeader });
          reqCount++;
        } catch {}
      }
    }

    // Push stakeholders
    const selectedSH = (report.stakeholders || []).filter((s) => selStakeholders.has(s.id));
    if (selectedSH.length > 0) {
      const newStakeholders: Stakeholder[] = selectedSH.map((s) => ({
        id: crypto.randomUUID(),
        name: s.name,
        role: s.role,
        team: "Handover Import",
        notes: `${s.concern} — Action: ${s.action}`,
      }));
      onStakeholdersAdded(newStakeholders);
      stakeholderCount = newStakeholders.length;
    }

    setPushed({ kanban: kanbanCount, requirements: reqCount, stakeholders: stakeholderCount });
    setPushing(false);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const toggle = (section: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(section)) next.delete(section); else next.add(section);
    return next;
  });

  const toggleSel = (set: Set<string>, setFn: any, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setFn(next);
  };

  const selectAll = (items: { id: string }[], setFn: any) =>
    setFn(new Set(items.map((i) => i.id)));
  const selectNone = (_: any, setFn: any) => setFn(new Set());

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 border-b border-border px-4 py-2.5 flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-accent" />
        <span className="text-sm font-semibold text-foreground">Handover Intelligence</span>
        <span className="text-[11px] text-muted-foreground">— inherit any project in 3 days</span>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── LEFT: Upload ── */}
        <div className="w-80 shrink-0 border-r border-border flex flex-col overflow-y-auto">
          <div className="p-4 space-y-3">
            <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Step 1 — Upload Documents</p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Drop any files left by the previous person: meeting notes, specs, exports, emails, Jira CSV, git logs, Slack exports.
            </p>

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-md px-4 py-6 text-center cursor-pointer transition-colors ${
                dragOver ? "border-accent bg-accent/5" : "border-border hover:border-accent/50 hover:bg-secondary/30"
              }`}
            >
              <Upload className="h-6 w-6 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-xs text-muted-foreground">Drag & drop files here</p>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">.txt .md .csv .json .log .eml and more</p>
            </div>
            <input ref={fileRef} type="file" multiple accept=".txt,.md,.csv,.json,.log,.xml,.html,.eml,.rtf,.msg" className="hidden" onChange={(e) => handleFiles(Array.from(e.target.files || []))} />

            {/* Uploaded file list */}
            {files.length > 0 && (
              <div className="space-y-1">
                {files.map((f) => (
                  <div key={f.name} className="flex items-center gap-2 text-[11px] border border-border rounded px-2 py-1 bg-secondary/20">
                    <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate text-card-foreground">{f.name}</span>
                    <span className="text-muted-foreground shrink-0">{(f.text.length / 1000).toFixed(1)}k</span>
                    <button onClick={() => removeFile(f.name)} className="text-muted-foreground hover:text-destructive transition-colors">
                      <XCircle className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Paste area */}
            <div className="space-y-1.5 border-t border-border pt-3">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Or paste content</p>
              <p className="text-[10px] text-muted-foreground">Copy-paste from PDFs, Word docs, emails, or Confluence pages.</p>
              <textarea
                className="w-full bg-background border border-border rounded px-2.5 py-1.5 text-xs placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/50 resize-none min-h-[100px]"
                placeholder="Paste project docs, meeting notes, emails here…"
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
              />
            </div>

            {error && (
              <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              onClick={handleAnalyse}
              disabled={analysing || (files.length === 0 && !pastedText.trim())}
              className="w-full flex items-center justify-center gap-1.5 bg-accent text-accent-foreground rounded px-3 py-2 text-xs font-semibold hover:bg-accent/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {analysing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {analysing ? "Analysing…" : "Analyse with AI"}
            </button>

            {/* Push section — appears after report */}
            {report && (
              <div className="border-t border-border pt-3 space-y-2">
                <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Step 3 — Push to Project</p>
                <p className="text-[10px] text-muted-foreground">Only checked items will be added. Nothing overwrites existing work.</p>

                <div className="space-y-1 text-[11px] text-muted-foreground">
                  <p className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-amber-500" />
                    {selInProgress.size} in-progress → Kanban
                  </p>
                  <p className="flex items-center gap-1">
                    <ListTodo className="h-3 w-3 text-sky-500" />
                    {selNotStarted.size} not started → Kanban + Requirements
                  </p>
                  <p className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-indigo-500" />
                    {selStakeholders.size} stakeholders → Stakeholders tab
                  </p>
                  {!baProjectId && (
                    <p className="text-[10px] text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Visit Requirements tab first to enable requirement sync
                    </p>
                  )}
                </div>

                {pushed ? (
                  <div className="rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-700 space-y-0.5">
                    <p className="font-semibold flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Pushed successfully</p>
                    <p>{pushed.kanban} stories → Kanban · {pushed.requirements} reqs · {pushed.stakeholders} stakeholders</p>
                  </div>
                ) : (
                  <button onClick={handlePush} disabled={pushing || (selInProgress.size + selNotStarted.size + selStakeholders.size === 0)}
                    className="w-full flex items-center justify-center gap-1.5 border border-accent/50 text-accent rounded px-3 py-1.5 text-xs font-semibold hover:bg-accent/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {pushing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                    Push to Project
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Report ── */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {!report && !analysing && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <ClipboardList className="h-14 w-14 text-muted-foreground/15" />
              <div>
                <p className="text-sm font-semibold text-muted-foreground">No handover analysed yet</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                  Upload what the previous person left behind — notes, exports, specs — and AI will map out exactly what's done, what's not, and what to focus on first.
                </p>
              </div>
              <div className="flex flex-col gap-1.5 text-[11px] text-muted-foreground text-left border border-border rounded-md px-4 py-3 bg-secondary/20">
                <p className="font-semibold text-foreground mb-1">Works great with:</p>
                <p>📄 Meeting notes & emails (paste as text)</p>
                <p>📊 Jira/Trello CSV exports</p>
                <p>📝 Word/Confluence docs (copy-paste content)</p>
                <p>🔀 Git log exports, GitHub issue exports</p>
                <p>💬 Slack export (channels.json or messages)</p>
              </div>
            </div>
          )}

          {analysing && (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
              <p className="text-sm text-muted-foreground">Reading everything and building the picture…</p>
            </div>
          )}

          {report && (
            <div className="max-w-2xl space-y-3">
              {/* Summary card */}
              <div className="rounded-md border border-border bg-card p-4 space-y-1.5">
                <p className="text-base font-bold text-card-foreground">{report.project_title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{report.summary}</p>
                {report.role_context && (
                  <p className="text-[11px] text-muted-foreground italic">{report.role_context}</p>
                )}
                <div className="flex gap-3 pt-1 flex-wrap">
                  {[
                    { label: "Done", count: report.completed?.length ?? 0, color: "text-emerald-600", icon: CheckCircle2 },
                    { label: "In Progress", count: report.in_progress?.length ?? 0, color: "text-amber-600", icon: Clock },
                    { label: "Not Started", count: report.not_started?.length ?? 0, color: "text-sky-600", icon: ListTodo },
                    { label: "Risks", count: report.risks?.length ?? 0, color: "text-rose-600", icon: AlertTriangle },
                  ].map(({ label, count, color, icon: Icon }) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <Icon className={`h-3.5 w-3.5 ${color}`} />
                      <span className={`text-xs font-semibold ${color}`}>{count}</span>
                      <span className="text-[11px] text-muted-foreground">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Focus this week */}
              <Section id="focus" label="🔥 Focus This Week" expanded={expanded} toggle={toggle}>
                <div className="space-y-2">
                  {(report.focus_this_week || []).map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 border border-border rounded px-3 py-2 bg-card">
                      <span className={`text-[10px] font-mono font-bold border rounded px-1.5 py-0.5 shrink-0 mt-0.5 ${DAY_COLOR[f.day]}`}>
                        Day {f.day}
                      </span>
                      <div>
                        <p className="text-xs font-semibold text-card-foreground">{f.action}</p>
                        <p className="text-[11px] text-muted-foreground">{f.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* In Progress */}
              <Section id="in_progress" label="🔄 In Progress" expanded={expanded} toggle={toggle}
                selectActions={{ onAll: () => selectAll(report.in_progress, setSelInProgress), onNone: () => selectNone(null, setSelInProgress) }}>
                <div className="space-y-1.5">
                  {(report.in_progress || []).map((item) => (
                    <ItemRow key={item.id} item={item} selected={selInProgress.has(item.id)}
                      onToggle={() => toggleSel(selInProgress, setSelInProgress, item.id)}>
                      {item.owner && <span className="text-[10px] text-muted-foreground">Owner: {item.owner}</span>}
                      {item.blocker && (
                        <span className="text-[10px] text-rose-600 flex items-center gap-0.5">
                          <AlertTriangle className="h-2.5 w-2.5" /> Blocker: {item.blocker}
                        </span>
                      )}
                    </ItemRow>
                  ))}
                  {(report.in_progress || []).length === 0 && <EmptyNote text="No in-progress items found" />}
                </div>
              </Section>

              {/* Not Started */}
              <Section id="not_started" label="📋 Not Started" expanded={expanded} toggle={toggle}
                selectActions={{ onAll: () => selectAll(report.not_started, setSelNotStarted), onNone: () => selectNone(null, setSelNotStarted) }}>
                <div className="space-y-1.5">
                  {(report.not_started || []).map((item) => (
                    <ItemRow key={item.id} item={item} selected={selNotStarted.has(item.id)}
                      onToggle={() => toggleSel(selNotStarted, setSelNotStarted, item.id)}>
                      {item.priority && (
                        <span className={`text-[10px] border rounded px-1.5 py-0.5 font-mono uppercase ${CONFIDENCE_COLOR[item.priority]}`}>
                          {item.priority}
                        </span>
                      )}
                    </ItemRow>
                  ))}
                  {(report.not_started || []).length === 0 && <EmptyNote text="No not-started items found" />}
                </div>
              </Section>

              {/* Completed */}
              <Section id="completed" label="✅ Completed" expanded={expanded} toggle={toggle}>
                <div className="space-y-1.5">
                  {(report.completed || []).map((item) => (
                    <div key={item.id} className="border border-border rounded px-3 py-2 bg-card/50 flex items-start gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-card-foreground">{item.title}</p>
                        <p className="text-[11px] text-muted-foreground">{item.detail}</p>
                      </div>
                      {item.confidence && (
                        <span className={`text-[10px] border rounded px-1.5 py-0.5 font-mono uppercase shrink-0 ${CONFIDENCE_COLOR[item.confidence]}`}>
                          {item.confidence}
                        </span>
                      )}
                    </div>
                  ))}
                  {(report.completed || []).length === 0 && <EmptyNote text="No completed items found" />}
                </div>
              </Section>

              {/* Stakeholders */}
              <Section id="stakeholders" label="👤 Key People" expanded={expanded} toggle={toggle}
                selectActions={{ onAll: () => selectAll(report.stakeholders, setSelStakeholders), onNone: () => selectNone(null, setSelStakeholders) }}>
                <div className="space-y-1.5">
                  {(report.stakeholders || []).map((s) => (
                    <div key={s.id} className="border border-border rounded px-3 py-2 bg-card flex items-start gap-2">
                      <input type="checkbox" checked={selStakeholders.has(s.id)}
                        onChange={() => toggleSel(selStakeholders, setSelStakeholders, s.id)}
                        className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs font-semibold text-card-foreground">{s.name}</p>
                          <span className="text-[10px] text-muted-foreground">{s.role}</span>
                          <span className={`text-[10px] border rounded px-1.5 py-0.5 font-mono ${ACTION_STYLE[s.action]}`}>{s.action}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{s.concern}</p>
                      </div>
                    </div>
                  ))}
                  {(report.stakeholders || []).length === 0 && <EmptyNote text="No stakeholders identified" />}
                </div>
              </Section>

              {/* Risks */}
              <Section id="risks" label="⚠️ Risks" expanded={expanded} toggle={toggle}>
                <div className="space-y-1.5">
                  {(report.risks || []).map((r) => (
                    <div key={r.id} className="border border-border rounded px-3 py-2 bg-card flex items-start gap-2.5">
                      <span className={`h-2 w-2 rounded-full shrink-0 mt-1.5 ${SEVERITY_DOT[r.severity]}`} />
                      <div>
                        <p className="text-xs text-card-foreground">{r.description}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5"><span className="font-semibold">Mitigation:</span> {r.mitigation}</p>
                      </div>
                    </div>
                  ))}
                  {(report.risks || []).length === 0 && <EmptyNote text="No risks identified" />}
                </div>
              </Section>

              {/* Open Questions */}
              {(report.open_questions || []).length > 0 && (
                <Section id="questions" label="❓ Open Questions" expanded={expanded} toggle={toggle}>
                  <div className="space-y-1">
                    {report.open_questions.map((q, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground border border-border rounded px-3 py-1.5 bg-card">
                        <span className="font-mono text-muted-foreground/50 shrink-0">{i + 1}.</span>
                        <span>{q}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function Section({ id, label, expanded, toggle, children, selectActions }: {
  id: string; label: string; expanded: Set<string>; toggle: (id: string) => void;
  children: React.ReactNode;
  selectActions?: { onAll: () => void; onNone: () => void };
}) {
  const isOpen = expanded.has(id);
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button onClick={() => toggle(id)}
        className="w-full flex items-center justify-between px-3 py-2 bg-secondary/20 hover:bg-secondary/40 transition-colors text-left">
        <span className="text-xs font-semibold text-card-foreground">{label}</span>
        <div className="flex items-center gap-2">
          {selectActions && isOpen && (
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <button onClick={selectActions.onAll} className="text-[10px] text-accent hover:underline">All</button>
              <button onClick={selectActions.onNone} className="text-[10px] text-muted-foreground hover:underline">None</button>
            </div>
          )}
          {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </button>
      {isOpen && <div className="p-3">{children}</div>}
    </div>
  );
}

function ItemRow({ item, selected, onToggle, children }: {
  item: HandoverItem; selected: boolean; onToggle: () => void; children?: React.ReactNode;
}) {
  return (
    <div className="border border-border rounded px-3 py-2 bg-card flex items-start gap-2">
      <input type="checkbox" checked={selected} onChange={onToggle} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="text-xs font-semibold text-card-foreground">{item.title}</p>
        <p className="text-[11px] text-muted-foreground">{item.detail}</p>
        {children && <div className="flex flex-wrap gap-2 mt-0.5">{children}</div>}
      </div>
    </div>
  );
}

function EmptyNote({ text }: { text: string }) {
  return <p className="text-[11px] text-muted-foreground/60 italic px-1">{text}</p>;
}
